import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { AgendaItem } from './api';
import type { Reminder } from './types';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DOW = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// Fecha local como 'YYYY-MM-DD' (las fechas de vencimiento son DATE locales).
function ymd(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

interface Props {
  items: AgendaItem[];
  onSelectThread: (taskId: string) => void;
  onSelectReminder: (reminder: Reminder) => void;
}

// Calendario mensual (tipo Google Calendar) con hilos + recordatorios con
// fecha de vencimiento. Abre por defecto el mes en curso.
export default function AgendaCalendar({ items, onSelectThread, onSelectReminder }: Props) {
  const now = new Date();
  const todayStr = ymd(now);
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });

  // Agrupar items por día.
  const byDay = new Map<string, AgendaItem[]>();
  items.forEach((it) => {
    const arr = byDay.get(it.fecha) ?? [];
    arr.push(it);
    byDay.set(it.fecha, arr);
  });

  // Rejilla: semanas que empiezan en lunes, con desbordes del mes anterior/siguiente.
  const first = new Date(cursor.y, cursor.m, 1);
  const startOffset = (first.getDay() + 6) % 7; // lunes = 0
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const weeks = Math.ceil((startOffset + daysInMonth) / 7);
  const start = new Date(cursor.y, cursor.m, 1 - startOffset);

  const cells: Date[] = [];
  for (let i = 0; i < weeks * 7; i += 1) {
    cells.push(new Date(cursor.y, cursor.m, 1 - startOffset + i));
  }

  const goPrev = () => setCursor(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }));
  const goNext = () => setCursor(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }));
  const goToday = () => setCursor({ y: now.getFullYear(), m: now.getMonth() });

  return (
    <div>
      <div style={styles.toolbar}>
        <div style={styles.navGroup}>
          <button type="button" onClick={goPrev} style={styles.navBtn} title="Mes anterior">‹</button>
          <button type="button" onClick={goNext} style={styles.navBtn} title="Mes siguiente">›</button>
          <button type="button" onClick={goToday} style={styles.todayBtn}>Hoy</button>
        </div>
        <div style={styles.monthLabel}>{MONTHS[cursor.m]} {cursor.y}</div>
        <div style={{ width: 120 }} />
      </div>

      <div style={styles.grid}>
        {DOW.map((d) => (
          <div key={d} style={styles.dowCell}>{d}</div>
        ))}
        {cells.map((date) => {
          const key = ymd(date);
          const inMonth = date.getMonth() === cursor.m;
          const isToday = key === todayStr;
          const dayItems = byDay.get(key) ?? [];
          const shown = dayItems.slice(0, 3);
          const extra = dayItems.length - shown.length;
          return (
            <div
              key={key}
              style={{
                ...styles.dayCell,
                ...(inMonth ? null : styles.dayOut),
                ...(isToday ? styles.dayToday : null),
              }}
            >
              <div style={{ ...styles.dayNum, ...(isToday ? styles.dayNumToday : null) }}>
                {date.getDate()}
              </div>
              <div style={styles.dayItems}>
                {shown.map((it) => {
                  const isThread = it.kind === 'thread' && it.task;
                  const label = isThread ? it.task!.asunto : it.reminder?.titulo ?? '';
                  const color = isThread ? (it.task!.assignee?.color ?? '#2563eb') : '#7c3aed';
                  return (
                    <button
                      key={`${it.kind}-${it.id}`}
                      type="button"
                      title={label}
                      onClick={() => {
                        if (isThread) onSelectThread(it.task!.id);
                        else if (it.reminder) onSelectReminder(it.reminder);
                      }}
                      style={styles.chip}
                    >
                      <span style={{ ...styles.chipDot, background: color }} />
                      <span style={styles.chipText}>{label}</span>
                    </button>
                  );
                })}
                {extra > 0 && <div style={styles.more}>+{extra} más</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.legend}>
        <span style={styles.legendItem}><span style={{ ...styles.chipDot, background: '#2563eb' }} /> Hilos</span>
        <span style={styles.legendItem}><span style={{ ...styles.chipDot, background: '#7c3aed' }} /> Recordatorios</span>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  navGroup: { display: 'flex', alignItems: 'center', gap: 6, width: 120 },
  navBtn: {
    width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff',
    color: '#334155', fontSize: 18, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  todayBtn: {
    height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff',
    color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  monthLabel: { fontSize: 17, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.3px', textTransform: 'capitalize' },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 1,
    background: '#e2e8f0', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden',
  },
  dowCell: {
    background: '#f8fafc', padding: '8px 6px', textAlign: 'center', fontSize: 11, fontWeight: 700,
    color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5,
  },
  dayCell: { background: '#fff', minHeight: 116, minWidth: 0, padding: 6, display: 'flex', flexDirection: 'column', gap: 4 },
  dayOut: { background: '#fbfcfd' },
  dayToday: { background: '#eff5ff' },
  dayNum: { fontSize: 12.5, fontWeight: 600, color: '#334155', alignSelf: 'flex-end', lineHeight: 1 },
  dayNumToday: {
    color: '#fff', background: '#2563eb', borderRadius: '50%', width: 22, height: 22,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  dayItems: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  chip: {
    display: 'flex', alignItems: 'center', gap: 5, width: '100%', minWidth: 0, maxWidth: '100%', background: '#f8fafc',
    border: '1px solid #eef2f7', borderRadius: 6, padding: '3px 6px', cursor: 'pointer', textAlign: 'left',
  },
  chipDot: { width: 7, height: 7, minWidth: 7, borderRadius: '50%' },
  chipText: { flex: 1, minWidth: 0, fontSize: 11.5, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  more: { fontSize: 11, color: '#64748b', fontWeight: 600, paddingLeft: 4 },
  legend: { display: 'flex', gap: 16, marginTop: 12 },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#64748b', fontWeight: 600 },
};
